import * as React from 'react';

import { useQuery } from '@apollo/client';

import { getCmsLocaleString } from 'js/lib/language';

import type {
  BrowseDomainValuePropositionQuery as BrowseDomainValuePropositionQueryResult,
  BrowseDomainValuePropositionQueryVariables,
} from 'bundles/browse-common/__generated__/contentfulTypes';
import DomainValuePropositionContext from 'bundles/browse-common/contexts/DomainValuePropositionContext';
import { BrowseDomainValueProposition } from 'bundles/browse-common/queries/BrowseDomainValueProposition';
import type { KnownDomainSlugs } from 'bundles/browse-common/types/domain';
import type { ValueProposition, ValuePropositionItem } from 'bundles/browse-common/types/valueProposition';
import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';

type Props = {
  domainSlug: KnownDomainSlugs;
  children: React.ReactNode;
};

const DomainValuePropositionProvider = (props: Props) => {
  const { domainSlug, children } = props;

  const { data, loading } = useQuery<
    BrowseDomainValuePropositionQueryResult,
    BrowseDomainValuePropositionQueryVariables
  >(BrowseDomainValueProposition, {
    skip: !domainSlug,
    variables: {
      locale: getCmsLocaleString(),
      domainSlug,
    },
    context: getGrowthCmsGraphqlContext(false),
  });

  const valuePropData = data?.browseDomainValuePropositionCollection?.items[0];

  const valuePropositionItems: ValuePropositionItem[] = [];
  if (valuePropData?.valuePropositionItemCollection?.items) {
    valuePropData.valuePropositionItemCollection.items.forEach((item) => {
      if (item) {
        valuePropositionItems.push({
          icon: item.icon,
          text: item.text,
        });
      }
    });
  }

  const domainValueProposition: ValueProposition | undefined | null = valuePropData
    ? {
        // domain name and slug are required properties in contentful
        domain: {
          name: valuePropData.domain?.name || '',
          slug: valuePropData.domain?.slug || '',
          description: valuePropData.domain?.name || '',
        },
        description: valuePropData.description,
        valuePropositionItems,
      }
    : null;

  return (
    <DomainValuePropositionContext.Provider value={{ domainValueProposition, loading }}>
      {children}
    </DomainValuePropositionContext.Provider>
  );
};

export default DomainValuePropositionProvider;
